import jwtDecode from 'jwt-decode';
import axios from 'src/utils/axios';
import { API_BASE_URL } from 'src/config';
import { trackRegistration } from 'src/utils/flito';

class AuthService {
  setAxiosInterceptors = ({ onLogout }) => {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      }
    );
  };

  handleAuthentication() {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return;
    }

    if (this.isValidToken(accessToken)) {
      this.setSession(accessToken);
    } else {
      this.setSession(null);
    }
  }

  loginWithEmail = (email) => new Promise((resolve, reject) => {
    axios.post(API_BASE_URL + '/account/login', { email })
      .then((response) => {
        if (response.data.user) {
          this.setSession(response.data.accessToken);
          resolve(response.data.user);
        } else {
          reject(response.data.error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  })

  sendUnlockOtp = (email) => this.sendCustomerOtp(email)

  loginWithUnlockOtp = (email, otp) => this.loginWithCustomerOtp(email, otp, '/unlock/verify-otp')

  /** Passwordless customer OTP (new white login + unlock). Creates customer with password "123" if needed. */
  sendCustomerOtp = (email) => new Promise((resolve, reject) => {
    axios.post(API_BASE_URL + '/account/otp/send', { email })
      .then((response) => {
        if (response.data && response.data.success) {
          resolve(response.data);
        } else {
          reject(new Error((response.data && response.data.message) || 'Failed to send code.'));
        }
      })
      .catch((error) => {
        reject(error);
      });
  })

  loginWithCustomerOtp = (email, otp, path = '/account/otp/verify') => new Promise((resolve, reject) => {
    axios.post(API_BASE_URL + path, { email, otp })
      .then((response) => {
        if (response.data && response.data.success && response.data.user) {
          this.setSession(response.data.accessToken);
          if (response.data.created) trackRegistration(response.data.user.email);
          resolve(response.data.user);
        } else {
          reject(new Error((response.data && response.data.message) || 'Verification failed.'));
        }
      })
      .catch((error) => {
        reject(error);
      });
  })

  loginWithEmailAndPassword = (email, password) => new Promise((resolve, reject) => {
    axios.post(API_BASE_URL + '/account/login-customer', { email, password })
      .then((response) => {
        if (response.data.user) {
          this.setSession(response.data.accessToken);
          resolve(response.data.user);
        } else {
          reject(response.data.message);
        }
      })
      .catch((error) => {
        reject(error);
      });
  })

  loginInWithToken = () => new Promise((resolve, reject) => {
    const token = this.getAccessToken();
    axios.post(API_BASE_URL + '/account/me', {token})
      .then((response) => {
        if (response.data.user) {
          resolve(response.data.user);
        } else {
          reject(response.data.error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  })

  logout = () => {
    this.setSession(null);
  }

  setSession = (accessToken) => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('accessToken');
      delete axios.defaults.headers.common.Authorization;
    }
  }

  getAccessToken = () => localStorage.getItem('accessToken');

  isValidToken = (accessToken) => {
    if (!accessToken) {
      return false;
    }

    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  }

  isAuthenticated = () => !!this.getAccessToken()
}

const authService = new AuthService();

export default authService;
